import { createContext, Dispatch, ReactNode, useCallback, useContext, useReducer } from 'react'
import { useAppDispatch } from 'store'
import { ContactInfo, LiveCallLog } from 'store/types/lead'
import { Lead as LeadActions } from 'store'
import { getLeadId } from './utils'

export type DialerClientStatus =
  | { status: 'connecting' }
  | { status: 'ready'; version: string }
  | { status: 'dialer_conncting' }
  | { status: 'error'; message: string; actions?: ReactNode }

interface State {
  isShowRightLiveCallPanel: boolean
  rightLiveCallPanelDefaultContact?: ContactInfo
  appBarCallStatus: AppBarCallStatus | null
  isCalling: boolean
  isSubmitting: boolean
  isDisable: boolean
  version: string | null
  dialerStatus: DialerClientStatus
}

const initialState: State = {
  isShowRightLiveCallPanel: false,
  rightLiveCallPanelDefaultContact: undefined,
  appBarCallStatus: null,
  isCalling: false,
  isSubmitting: false,
  isDisable: true,
  version: null,
  dialerStatus: { status: 'connecting' },
}

export interface AppBarCallStatus {
  message: string
  leadId: string | null
  resettable?: boolean
}

type Action =
  | { type: 'SHOW_RIGHT_PANEL'; defaultContact?: ContactInfo }
  | { type: 'HIDE_RIGHT_PANEL' }
  | { type: 'SHOW_APPBAR_STATUS'; status: AppBarCallStatus }
  | { type: 'HIDE_APPBAR_STATUS' }
  | { type: 'SET_IS_CALLING'; isCalling: boolean }
  | { type: 'SET_IS_SUBMITTING'; isSubmitting: boolean }
  | { type: 'SET_IS_DISABLE'; isDisable: boolean }
  | { type: 'SET_DIALER_STATUS'; dialerStatus: DialerClientStatus }

const dialerUIReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SHOW_RIGHT_PANEL':
      return { ...state, isShowRightLiveCallPanel: true, rightLiveCallPanelDefaultContact: action.defaultContact }
    case 'HIDE_RIGHT_PANEL':
      return { ...state, isShowRightLiveCallPanel: false, rightLiveCallPanelDefaultContact: undefined }
    case 'SHOW_APPBAR_STATUS':
      return { ...state, appBarCallStatus: action.status }
    case 'HIDE_APPBAR_STATUS':
      return { ...state, appBarCallStatus: null }
    case 'SET_IS_CALLING':
      return { ...state, isCalling: action.isCalling }
    case 'SET_IS_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting }
    case 'SET_IS_DISABLE':
      return { ...state, isDisable: action.isDisable }
    case 'SET_DIALER_STATUS':
      return { ...state, dialerStatus: action.dialerStatus }
  }
}

interface ContextProps {
  state: State
  dispatch: Dispatch<Action>
  showOutcomeForm: (liveCallLog: LiveCallLog) => void
}

const Context = createContext<ContextProps | undefined>(undefined)

export const DialerUIProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dialerUIReducer, initialState)
  const appDispatch = useAppDispatch()

  const showOutcomeForm = useCallback(
    (liveCallLog: LiveCallLog) => {
      const currentLeadId = getLeadId()
      if (currentLeadId && currentLeadId == liveCallLog.leadId) {
        appDispatch(
          LeadActions.addLeadDetailWindow({
            data: {
              leadId: liveCallLog.leadId || '',
              initialValues: liveCallLog,
              referenceId: liveCallLog.liveCallId,
            },
            type: 'liveCall',
            windowState: 'active',
            isPriority: true,
            mode: 'update',
          }),
        )
      }
    },
    [appDispatch],
  )

  return <Context.Provider value={{ state, dispatch, showOutcomeForm }}>{children}</Context.Provider>
}

export const useDialerUIContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useDialerUIContext must be used within DialerUIProvider')
  }
  return context
}
